import { useEffect, type JSX, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { acquireApiToken } from '../auth/authService.js';

const AVATAR_BG = ['#DBE7FE', '#DCFCE7', '#FEF3C7', '#EDE9FE', '#FCE7F3', '#E0F2FE'];

/**
 * 레이어드 확인 모달 — native window.confirm() 대체.
 * (Outlook/Teams 임베드 iframe에서 confirm/alert 차단되므로 커스텀 모달 사용, z-index는 기존 오버레이(50) 위)
 */
export function ConfirmDialog({ title, message, confirmLabel = '확인', danger = false, busy = false, onConfirm, onCancel }: {
  title: string; message?: string; confirmLabel?: string; danger?: boolean; busy?: boolean; onConfirm: () => void; onCancel: () => void;
}): JSX.Element {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(16,24,40,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 22, width: 360, maxWidth: '94%', boxShadow: '0 24px 60px rgba(16,24,40,.35)', animation: 'dxpop .2s ease' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: message ? 8 : 16 }}>{title}</div>
        {message && <div style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.6, marginBottom: 18, wordBreak: 'break-word' }}>{message}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="dx-btn dx-btn-ghost" onClick={onCancel}>취소</button>
          <button className={`dx-btn ${danger ? 'dx-btn-danger' : 'dx-btn-primary'}`} disabled={busy} onClick={onConfirm}>{busy ? '처리 중…' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/** upn 이 있으면 프로필 사진(/api/photo)을 받아 표시, 없거나 미등록이면 이니셜 폴백 */
async function fetchPhoto(upn: string): Promise<string | null> {
  const token = await acquireApiToken();
  const res = await fetch(`/api/photo?upn=${encodeURIComponent(upn)}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob.size) return null;
  return URL.createObjectURL(blob);
}

export function Avatar({ name, index = 0, size = 36, upn }: { name: string; index?: number; size?: number; upn?: string }): JSX.Element {
  const photo = useQuery({
    queryKey: ['photo', upn],
    queryFn: () => fetchPhoto(upn as string),
    enabled: Boolean(upn),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  if (upn && photo.data) {
    return <img src={photo.data} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', background: AVATAR_BG[index % AVATAR_BG.length],
      color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size <= 30 ? 11 : 12, flexShrink: 0,
    }}>
      {name.slice(1) || name.slice(0, 1)}
    </span>
  );
}

export function Spinner({ size = 30 }: { size?: number }): JSX.Element {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size, border: '3px solid #DBE7FE',
      borderTopColor: '#2563EB', borderRadius: '50%', animation: 'dxspin .8s linear infinite',
    }} />
  );
}

export function Modal({ width = 440, onClose, children }: { width?: number; onClose: () => void; children: ReactNode }): JSX.Element {
  return (
    <div className="dx-overlay" onClick={onClose}>
      <div className="dx-modal" style={{ width, maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * 머리글·본문·바닥글이 있는 팝업 — 본문만 스크롤되고 버튼은 항상 보임. Esc·바깥 클릭으로 닫힘.
 * (Modal 은 내용이 직접 여백을 잡아야 하는 하위 호환용)
 */
export function ModalFrame({ title, extra, sub, width = 480, onClose, footer, children }: {
  title: ReactNode; extra?: ReactNode; sub?: ReactNode; width?: number; onClose: () => void; footer?: ReactNode; children: ReactNode;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="dx-overlay" onClick={onClose}>
      <div className="dx-modal dx-mf" role="dialog" aria-modal="true" style={{ width, maxWidth: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="dx-mf-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}><h3 className="dx-mf-title">{title}</h3>{extra}</div>
            {sub && <div className="dx-mf-sub">{sub}</div>}
          </div>
          <button className="dx-mf-x" onClick={onClose} aria-label="닫기">×</button>
        </div>
        <div className="dx-mf-body">{children}</div>
        {footer && <div className="dx-mf-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Toast({ message, onDone }: { message: string | null; onDone: () => void }): JSX.Element | null {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onDone, 2600);
    return () => clearTimeout(t);
  }, [message, onDone]);
  if (!message) return null;
  return (
    <div style={{
      position: 'fixed', left: '50%', bottom: 28, transform: 'translateX(-50%)',
      background: '#111827', color: '#fff', padding: '11px 18px', borderRadius: 999,
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600,
      boxShadow: '0 10px 30px rgba(16,24,40,.3)', animation: 'dxtoast .25s ease', zIndex: 60,
    }}>
      <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#22C55E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
      </span>
      {message}
    </div>
  );
}
